import React from 'react';
import { UserAccount } from '../types';
import { checkRestrictedAreaAccess } from '../utils/auth';

interface AccessControlGuardProps {
  currentUser?: UserAccount;
  onOpenLoginModal: () => void;
  requiredRole?: 'writer' | 'authorizer' | 'admin';
  restrictedAreaId?: string;
  title?: string;
  description?: string;
  children: React.ReactNode;
}

export const AccessControlGuard: React.FC<AccessControlGuardProps> = ({
  currentUser,
  onOpenLoginModal,
  requiredRole = 'writer',
  restrictedAreaId,
  title = 'Secretariat Authorization Required',
  description = 'This exclusive kingdom publishing suite and document management portal requires authorized Secretariat credentials (Writer, Authorizer, or Administrator).',
  children,
}) => {
  // Check if user is logged in and meets role hierarchy
  const roleHierarchy: Record<string, number> = {
    writer: 1,
    authorizer: 2,
    admin: 3,
  };

  const userLevel = currentUser ? roleHierarchy[currentUser.role] || 0 : 0;
  const requiredLevel = roleHierarchy[requiredRole] || 1;

  const meetsRoleLevel = currentUser && userLevel >= requiredLevel;

  // Suspended Account Check
  if (currentUser && currentUser.status === 'suspended') {
    return (
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-950 text-slate-100 min-h-[75vh]">
        <div className="max-w-md w-full bg-slate-900 border-2 border-red-600 rounded-3xl p-8 shadow-2xl text-center space-y-6">
          <div className="w-16 h-16 bg-red-600/20 border border-red-500 rounded-full flex items-center justify-center mx-auto text-3xl">
            🚫
          </div>

          <div className="space-y-2">
            <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded bg-red-950 text-red-300 border border-red-800">
              Account Suspended By Administrator
            </span>
            <h2 className="text-xl font-serif font-black text-red-300">
              Access Restricted
            </h2>
            <p className="text-xs text-gray-300 leading-relaxed">
              Your account ({currentUser.email}) has been deactivated or suspended by the Global Administrator. You cannot perform administrative or secretariat operations.
            </p>
          </div>

          <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 text-left space-y-2">
            <div className="text-[11px] font-bold text-gray-400">Suspended Session:</div>
            <div className="flex items-center justify-between text-xs">
              <span>User:</span>
              <strong className="text-red-300">{currentUser.name}</strong>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span>Status:</span>
              <strong className="text-red-400 font-mono uppercase">Deactivated / Suspended</strong>
            </div>
          </div>

          <button
            onClick={onOpenLoginModal}
            className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-amber-300 font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer border border-amber-400/30"
          >
            Switch Account
          </button>
        </div>
      </div>
    );
  }

  // Check granular restricted area access if specified
  const areaCheck = restrictedAreaId && currentUser 
    ? checkRestrictedAreaAccess(currentUser, restrictedAreaId)
    : { allowed: true };

  // Password reset mandatory check
  if (currentUser && (currentUser.mustChangePassword || !currentUser.initialLoginCompleted)) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-950 text-slate-100 min-h-[75vh]">
        <div className="max-w-md w-full bg-slate-900 border-2 border-amber-500 rounded-3xl p-8 shadow-2xl text-center space-y-6">
          <div className="w-16 h-16 bg-amber-400/20 border border-amber-400/40 rounded-full flex items-center justify-center mx-auto text-3xl">
            🔒
          </div>

          <div className="space-y-2">
            <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded bg-amber-950 text-amber-300 border border-amber-800">
              Mandatory Security Requirement
            </span>
            <h2 className="text-xl font-serif font-black text-amber-300">
              Initial Password Reset Required
            </h2>
            <p className="text-xs text-gray-300 leading-relaxed">
              {currentUser.forcePasswordResetReason ||
                'For account security compliance, you must update your initial default password before accessing restricted secretariat areas.'}
            </p>
          </div>

          <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 text-left space-y-2">
            <div className="text-[11px] font-bold text-gray-400">Account Details:</div>
            <div className="flex items-center justify-between text-xs">
              <span>User:</span>
              <strong className="text-amber-300">{currentUser.name}</strong>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span>Email:</span>
              <strong className="text-emerald-400">{currentUser.email}</strong>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <button
              onClick={onOpenLoginModal}
              className="w-full py-3 bg-[#0F5132] hover:bg-[#08331f] text-amber-300 font-extrabold text-xs uppercase tracking-wider rounded-xl shadow-lg transition-all cursor-pointer border border-amber-400/50 flex items-center justify-center gap-2"
            >
              <span>🔑 Reset Password Now</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Restricted Area Permission Denied check
  if (currentUser && restrictedAreaId && !areaCheck.allowed) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-950 text-slate-100 min-h-[75vh]">
        <div className="max-w-md w-full bg-slate-900 border border-red-500/40 rounded-3xl p-8 shadow-2xl text-center space-y-6">
          <div className="w-16 h-16 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center mx-auto text-3xl">
            ⛔
          </div>

          <div className="space-y-2">
            <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded bg-red-950 text-red-300 border border-red-800">
              Restricted Area Access Denied
            </span>
            <h2 className="text-xl font-serif font-black text-red-300">
              Specific Clearance Required
            </h2>
            <p className="text-xs text-gray-300 leading-relaxed">
              {areaCheck.reason || 'Your account has not been granted specific permission for this restricted area by the administrator.'}
            </p>
          </div>

          <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 text-left space-y-2">
            <div className="text-[11px] font-bold text-gray-400">Current User:</div>
            <div className="flex items-center justify-between text-xs">
              <span>Identity:</span>
              <strong className="text-amber-300">{currentUser.name}</strong>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span>Role:</span>
              <strong className="text-emerald-400 uppercase">{currentUser.role}</strong>
            </div>
          </div>

          <div className="text-[11px] text-gray-400">
            Please contact the Global Administrator (admin@erueuzairue.org) to request access clearance for this restricted module.
          </div>
        </div>
      </div>
    );
  }

  if (meetsRoleLevel) {
    return <>{children}</>;
  }

  return (
    <div className="flex-1 flex items-center justify-center p-6 bg-slate-950 text-slate-100 min-h-[75vh]">
      <div className="max-w-md w-full bg-slate-900 border border-amber-500/40 rounded-3xl p-8 shadow-2xl text-center space-y-6">
        <div className="w-16 h-16 bg-amber-400/10 border border-amber-400/30 rounded-full flex items-center justify-center mx-auto text-3xl">
          🔐
        </div>

        <div className="space-y-2">
          <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded bg-amber-950 text-amber-300 border border-amber-800">
            RBAC Access Guard • Level {requiredRole.toUpperCase()}
          </span>
          <h2 className="text-xl font-serif font-black text-amber-300">{title}</h2>
          <p className="text-xs text-gray-300 leading-relaxed">
            {description}
          </p>
        </div>

        <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800 text-left space-y-2">
          <div className="text-[11px] font-bold text-gray-400">Current Session Status:</div>
          <div className="flex items-center justify-between text-xs">
            <span>Identity:</span>
            <strong className="text-amber-300">{currentUser ? currentUser.name : 'Public Surfer / Visitor'}</strong>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>Assigned Role:</span>
            <strong className="text-emerald-400 uppercase">{currentUser ? currentUser.role : 'Guest (Public View)'}</strong>
          </div>
        </div>

        <div className="space-y-3 pt-2">
          <button
            onClick={onOpenLoginModal}
            className="w-full py-3 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-lg transition-all cursor-pointer"
          >
            Sign In with Secretariat Credentials
          </button>
          
          <div className="text-[11px] text-gray-400">
            Note: The main website (Festival Portal, Calendar, News, Store, Schedule) remains publicly available to all visitors and surfers.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessControlGuard;

